import { useMemo } from 'react';
import { SiteConfigurationTemplate } from '../components/site-configuration-template/types';

export type UsageCategory = 'Asset' | 'Alarm Rule' | 'Widget' | 'Site Preview';

const CATEGORY_ORDER: UsageCategory[] = ['Asset', 'Alarm Rule', 'Widget', 'Site Preview'];

/** Builds a map of sourceTagId --> set of resource categories that use the tag. */
export const buildUsageCategories = (
  template: SiteConfigurationTemplate
): Record<string, UsageCategory[]> => {
  const index: Record<string, Set<UsageCategory>> = {};

  const add = (tagId: string | null | undefined, category: UsageCategory) => {
    if (!tagId) return;
    if (!index[tagId]) index[tagId] = new Set();
    index[tagId].add(category);
  };

  for (const asset of template.assets) {
    for (const assigned_tag of asset.assignedTags ?? [])
      add(assigned_tag.tagIdempotencyKey, 'Asset');
    for (const alarm_tag of asset.assignedAlarmTags ?? [])
      add(alarm_tag.tagIdempotencyKey, 'Asset');
  }

  for (const rule of template.alarmRules) {
    for (const condition of rule.conditions ?? []) add(condition.tagIdempotencyKey, 'Alarm Rule');
  }

  for (const widget of template.widgets) {
    for (const tag of widget.tags ?? []) add(tag.tagIdempotencyKey, 'Widget');
  }

  for (const tag of template.sitePreview?.tags ?? []) add(tag.tagIdempotencyKey, 'Site Preview');

  // Return categories in a stable display order
  return Object.fromEntries(
    Object.entries(index).map(([tagId, cats]) => [tagId, CATEGORY_ORDER.filter((c) => cats.has(c))])
  );
};

/** Which resource categories (asset/alarm rule/widget/site preview) reference each source tag. */
export const useUsageCategories = (
  template: SiteConfigurationTemplate
): Record<string, UsageCategory[]> => useMemo(() => buildUsageCategories(template), [template]);
